import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollReveal from '../../components/ui/ScrollReveal';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const credentials = [
  { icon: 'ri-award-line', title: 'Level 3 Personal Trainer', body: 'REPs Certified, fully insured and qualified to the highest industry standard.' },
  { icon: 'ri-heart-pulse-line', title: 'Nutrition Coach', body: 'Qualified in sports nutrition and dietary planning for body composition change.' },
  { icon: 'ri-run-line', title: 'Professional Dance Background', body: 'Former professional dancer — bringing elite-level body awareness to every programme.' },
  { icon: 'ri-mental-health-line', title: 'Mindset Coaching', body: 'Trained in behaviour change and habit formation for sustainable, lasting results.' },
];

const approach = [
  { num: '01', title: 'Strength', body: 'Compound lifts, progressive overload and functional movement patterns form the backbone of every programme.' },
  { num: '02', title: 'Nutrition', body: 'Food is fuel. Laura builds realistic, flexible nutrition strategies that fit your life — not the other way around.' },
  { num: '03', title: 'Mindset', body: 'The mental game is half the battle. Laura coaches the whole person, not just the body.' },
  { num: '04', title: 'Accountability', body: 'Regular check-ins, honest feedback and genuine investment in your progress — because you deserve a coach who cares.' },
];

export default function About() {
  useSEO({
    title: 'About Laura Martin | Personal Trainer London',
    description:
      "Meet Laura Martin — one of London's leading personal trainers. Former professional dancer turned elite PT. Discover her coaching philosophy.",
  });

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] lg:h-[70vh] lg:min-h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img src="/images/Laura_5.png" alt="Laura Martin personal trainer London" className="w-full h-full object-cover object-[50%_10%]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917]/80 via-[#1c1917]/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #faf8f5 0%, rgba(250,248,245,0.35) 18%, transparent 42%)' }} />
          </div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36">
            <div className="absolute bottom-0 pb-10 lg:pb-24 max-w-3xl">
              <div className="flex items-center gap-4 mb-5">
                <span className="w-8 h-px bg-[#c9a96e]" />
                <span className="text-[#c9a96e] text-[10px] font-sans font-semibold tracking-[0.32em] uppercase">About Laura</span>
              </div>
              <h1 className="text-white" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(3rem, 6vw, 5rem)', fontWeight: 600, lineHeight: 1.0 }}>
                More Than a Trainer.
                <br />
                <em className="text-[#c9a96e] not-italic">Your Biggest Advocate.</em>
              </h1>
            </div>
          </div>
        </section>

        {/* Bio */}
        <section className="bg-[#faf8f5] py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <ScrollReveal>
                <div className="sticky top-24 rounded-2xl overflow-hidden h-[560px] shadow-xl shadow-stone-200">
                  <img src="/images/Laura_3_edited.jpg" alt="Laura Martin personal trainer London coaching client" loading="lazy" className="w-full h-full object-cover object-top" />
                </div>
              </ScrollReveal>

              <div>
                <ScrollReveal delay={100}>
                  <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                    Laura Martin is one of the most trusted names for personal trainer London clients rely on for intelligent, sustainable results. Her route into fitness was anything but conventional. Before becoming a PT, Laura spent years as a professional dancer, performing at elite level and developing exceptional control, movement literacy, and body awareness.
                  </p>
                </ScrollReveal>
                <ScrollReveal delay={170}>
                  <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                    When she transitioned into coaching, she brought that precision directly into strength training London programmes tailored to real life. Laura does not hand over generic templates. Every client receives a bespoke strategy rooted in progressive overload, performance-led programming, and practical nutrition guidance.
                  </p>
                </ScrollReveal>
                <ScrollReveal delay={240}>
                  <p className="text-stone-600 font-sans text-base leading-relaxed mb-10">
                    Whether you train 1-2-1 in London, work remotely with Laura as your online fitness coach, or pair sessions with nutrition support, her approach stays consistent: technical excellence, honest accountability, and genuine care for long-term change.
                  </p>
                </ScrollReveal>
                <ScrollReveal delay={310}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {credentials.map((c) => (
                      <div key={c.title} className="bg-white rounded-xl p-5 border border-stone-200 hover:border-[#c9a96e]/30 hover:shadow-sm transition-all duration-300">
                        <i className={`${c.icon} text-[#c9a96e] text-xl mb-3 block`} />
                        <h3 className="text-stone-900 font-sans text-sm font-semibold mb-1.5">{c.title}</h3>
                        <p className="text-stone-500 font-sans text-xs leading-relaxed">{c.body}</p>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Approach */}
        <section className="bg-stone-100 py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-14">
              <div className="flex items-center gap-4 mb-5">
                <span className="w-8 h-px bg-[#c9a96e]" />
                <span className="text-[#c9a96e] text-[10px] font-sans font-semibold tracking-[0.32em] uppercase">My Approach</span>
              </div>
              <h2 className="text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)', fontWeight: 600, lineHeight: 1.05 }}>
                The Four Pillars
                <br />
                <em className="text-[#c9a96e] not-italic">Of Transformation.</em>
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {approach.map((a, i) => (
                <ScrollReveal key={a.num} delay={i * 80}>
                  <div className="bg-white rounded-2xl p-8 h-full border border-stone-200 hover:border-[#c9a96e]/30 hover:shadow-md transition-all duration-300 group">
                    <span className="text-[#c9a96e]/25 font-display text-5xl font-bold block mb-5 group-hover:text-[#c9a96e]/50 transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {a.num}
                    </span>
                    <h3 className="text-stone-900 text-xl mb-3 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{a.title}</h3>
                    <p className="text-stone-500 font-sans text-sm leading-relaxed">{a.body}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#faf8f5] py-16 lg:py-24 text-center border-t border-stone-200">
          <ScrollReveal>
            <div className="max-w-xl mx-auto px-6">
              <h2 className="text-stone-900 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.5rem', fontWeight: 600 }}>
                Ready to Start Your Journey?
              </h2>
              <p className="text-stone-500 font-sans text-sm leading-relaxed mb-8">
                Book a free no-obligation consultation and discover exactly how Laura can help you reach your goals.
              </p>
              <Link to="/contact" className="inline-block px-9 py-4 bg-[#c9a96e] text-white text-[10px] font-sans font-bold tracking-[0.28em] uppercase rounded-full hover:bg-[#a8863f] hover:shadow-xl hover:shadow-[#c9a96e]/20 hover:-translate-y-0.5 transition-all duration-300">
                Book Free Consultation
              </Link>
            </div>
          </ScrollReveal>
        </section>
      </main>
      <Footer />
    </div>
  );
}
